import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/api/axios-instance";

export const PRODUCT_TYPES = [
  "TELE_HEALTH",
  "ROCKET_LABS",
  "ROCKET_PHARMACY",
] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const USER_SCOPES = ["SINGLE_USER", "MULTI_USER"] as const;
export type UserScope = (typeof USER_SCOPES)[number];

export const PROFESSIONAL_TYPES = [
  "COUNSELING_PSYCHOLOGIST",
  "COUNSELLOR",
  "REGISTERED_CLINICAL_PSYCHOLOGIST",
  "REGISTERED_CLINICAL_PSYCHOLOGIST_ASSOCIATE",
  "PSYCHIATRIST_MD",
  "PSYCHIATRIST_DNB",
  "OBGYN_MS",
  "SURGEON_MS",
  "GENERAL_PHYSICIAN",
] as const;

// Participant/ownership model (mirrors the server's participant-model.ts).
// A slot is a keyed participant role with cardinality; the key is referenced by
// a bundle's session_types[].participant. Null config → the Individual default.
export interface ParticipantSlot {
  key: string;
  label: string;
  min: number;
  max: number;
}
export interface ParticipantConfig {
  purchaser_is_participant: boolean; // false = gift / on-behalf (buyer not a participant)
  slots: ParticipantSlot[];
}

export interface Product {
  id: string;
  name: string;
  description: string | null;
  unit_price: number;
  category: string;
  sub_category: string | null;
  type: ProductType;
  user_scope: UserScope;
  status: "ACTIVE" | "INACTIVE";
  notify_user_template_type: string;
  notify_professional_template_type: string;
  professional_type: string[];
  available_session_minutes: number[];
  participant_config?: ParticipantConfig | null;
  created_at: string;
  updated_at: string;
}

export interface ProductInput {
  name: string;
  description?: string;
  unit_price: number;
  category: string;
  sub_category?: string;
  type: ProductType;
  user_scope: UserScope;
  status?: "ACTIVE" | "INACTIVE";
  notify_user_template_type: string;
  notify_professional_template_type: string;
  professional_type?: string[];
  available_session_minutes?: number[];
  participant_config?: ParticipantConfig | null;
}

export async function getProducts(): Promise<Product[]> {
  const r = await axiosInstance.get("/product");
  return r.data ?? [];
}
export function useProducts() {
  return useQuery({ queryKey: ["products"], queryFn: getProducts });
}
export async function createProduct(data: ProductInput) {
  return (await axiosInstance.post("/product", data)).data;
}
export async function updateProduct(id: string, data: Partial<ProductInput>) {
  return (await axiosInstance.patch(`/product/${id}`, data)).data;
}
